import React from 'react';
import marketingTheme from '../themes/marketingTheme';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(rgba(36, 36, 42, 0.8), rgba(36, 36, 42, 0.8)), url("/images/machine_learning.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: '400px',
  display: 'flex',
  alignItems: 'center',
  color: 'white',
}));

const ServiceSection = styled(Paper)(({ theme }) => ({
  padding: '48px',
  margin: '32px 0',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
  border: '1px solid #E5E7EB',
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: '32px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  border: '1px solid #E5E7EB',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  },
}));

const MachineLearningConsulting = () => {
  const features = [
    {
      icon: <PsychologyIcon sx={{ fontSize: '48px', color: marketingTheme.palette.primary.main }} />,
      title: 'AI Strategy & Planning',
      description: 'Deep knowledge of state-of-the-art techniques to design custom AI solutions.',
    },
    {
      icon: <AutoGraphIcon sx={{ fontSize: '48px', color: marketingTheme.palette.primary.main }} />,
      title: 'Data Analytics Pipeline',
      description: 'Industrial streaming data analytics for real-time insights and decision making.',
    },
    {
      icon: <PrecisionManufacturingIcon sx={{ fontSize: '48px', color: marketingTheme.palette.primary.main }} />,
      title: 'Process Optimization',
      description: 'Empirical comprehension of complex industrial processes for improved efficiency.',
    },
  ];

  const capabilities = [
    'Computer vision and image processing',
    'Predictive maintenance algorithms',
    'Real-time anomaly detection',
    'Process optimization models',
    'Custom neural network architectures',
    'Edge AI deployment solutions',
    'Industrial IoT data integration',
    'Automated quality control systems',
  ];

  return (
    <Box sx={{ pt: 10, backgroundColor: '#F9FAFB' }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '36px', md: '48px' },
                  fontWeight: 700,
                  mb: 2,
                  color: 'white',
                }}
              >
                Machine Learning Solutions
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: '20px',
                  fontWeight: 400,
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 4,
                }}
              >
                Extracting wisdom from high volumes of raw data.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Features Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <FeatureCard>
                <Box sx={{ mb: 3 }}>{feature.icon}</Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1F2937',
                    mb: 2,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                  }}
                >
                  {feature.description}
                </Typography>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>

        {/* Overview Section */}
        <ServiceSection>
          <Typography
            variant="h3"
            sx={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#1F2937',
              mb: 4,
              textAlign: 'center',
            }}
          >
            Overview
          </Typography>
          
          <Typography sx={{ color: '#6B7280', mb: 4, lineHeight: 1.6 }}>
            We drive automated action from visual data.
          </Typography>

          {/* Process Segmentation Image */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src="/images/process_segmentation.png"
              alt="AI Vision for Production Flow"
              style={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: '8px',
              }}
            />
          </Box>

          <Typography sx={{ color: '#6B7280', mb: 4, lineHeight: 1.6 }}>
            The vast amounts of data generated by industrial machines and workflows is often 
            underutilized, resulting in lost opportunities for efficiency and cost savings.
          </Typography>

          {/* Data Wisdom Pyramid */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src="/images/data_information_knowledge_wisdom.png"
              alt="Data, Information, Knowledge, Wisdom Pyramid. DKIW"
              style={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: '8px',
              }}
            />
          </Box>

          <Typography sx={{ color: '#6B7280', mb: 4, lineHeight: 1.6 }}>
            Unlock the full potential of your industrial data with our machine learning solutions. 
            The MotionLogic team has strong research and development expertise, and a deep knowledge of 
            state-of-the-art techniques to design, build, and deploy bespoke machine learning 
            and artificial intelligence solutions.
          </Typography>

          <Typography sx={{ color: '#6B7280', mb: 4, lineHeight: 1.6 }}>
            Increase efficiency and flexibility, and reduce downtime. Our machine learning 
            solutions enable empirical comprehension of complex industrial processes, and 
            provide actionable insights to improve decision-making.
          </Typography>

          {/* Video Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box
              sx={{
                width: '80%',
                aspectRatio: '16/9',
                margin: '0 auto',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/1stQbXi217I?start=3&autoplay=1&loop=1&mute=1&rel=0"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ border: 'none' }}
              />
            </Box>
          </Box>
        </ServiceSection>

        {/* Capabilities Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h4"
              sx={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#1F2937',
                mb: 3,
              }}
            >
              Our Machine Learning Capabilities
            </Typography>
            <List>
              {capabilities.map((capability, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: marketingTheme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={capability}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: '#6B7280',
                        fontSize: '16px',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h5"
              sx={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#1F2937',
                mb: 3,
              }}
            >
              Industrial Streaming Data Analytics Pipeline
            </Typography>
            <Box sx={{ textAlign: 'center' }}>
              <img
                src="/images/data_driven_industrial_automation_light.png"
                alt="Streaming Data Pipelines"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: 'center',
            backgroundColor: 'white',
            p: 6,
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#1F2937',
              mb: 2,
            }}
          >
            Ready to harness the power of AI?
          </Typography>
          <Typography
            sx={{
              color: '#6B7280',
              mb: 4,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Let us help you transform your industrial data into actionable insights with 
            custom machine learning solutions.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: marketingTheme.palette.primary.main,
                '&:hover': { backgroundColor: marketingTheme.palette.primary.dark },
                px: 4,
                py: 1.5,
              }}
            >
              Learn More
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                color: '#1F2937',
                borderColor: '#D1D5DB',
                '&:hover': { backgroundColor: '#F3F4F6' },
                px: 4,
                py: 1.5,
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MachineLearningConsulting;
